import { useEffect, useState } from 'react';
import { useApi } from '../../lib/useApi.js';
import {
  Drawer,
  FormField,
  TextInput,
  DateInput,
  Select,
  Textarea,
  ChipMultiSelect,
  DrawerForm,
  DrawerFormActions,
} from '../../components/index.js';

const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'critical'];
const STATUS_OPTIONS = [
  { value: 'not-started', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'at-risk', label: 'At risk' },
  { value: 'complete', label: 'Complete' },
];

const DEFAULTS = (defaults = {}) => ({
  title: '',
  type: 'epic',
  projectId: defaults.projectId || '',
  ownerUserId: defaults.ownerUserId || '',
  sprintId: '',
  startDate: '',
  endDate: '',
  priority: 'high',
  status: 'not-started',
  linkedObjectiveId: '',
  missionKpiId: '',
  description: '',
  acceptanceSummary: '',
  tags: [],
});

export function NewEpicDrawer({ open, onClose, onSubmit, submitting, defaults = {} }) {
  const projectsRes = useApi('/projects', { query: { limit: 100 } });
  const sprintsRes = useApi('/sprints', { query: { limit: 60 } });
  const objectivesRes = useApi('/objectives', { query: { limit: 60 } });
  const missionKpisRes = useApi('/mission-kpis', { query: { limit: 60 } });
  const usersRes = useApi('/users', { query: { limit: 100 } });

  const [draft, setDraft] = useState(DEFAULTS(defaults));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setDraft(DEFAULTS(defaults));
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const projects = projectsRes.data?.data || [];
  const sprints = (sprintsRes.data?.data || []).filter(
    (s) => !draft.projectId || s.projectId === draft.projectId,
  );
  const objectives = objectivesRes.data?.data || [];
  const missionKpis = missionKpisRes.data?.data || [];
  const users = usersRes.data?.data || [];
  const selectedProject = projects.find((p) => p.id === draft.projectId);

  function update(field, value) {
    setDraft((d) => ({ ...d, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: null }));
  }

  function validate() {
    const next = {};
    if (!draft.title.trim()) next.title = 'Title is required.';
    if (!draft.projectId) next.projectId = 'Pick the project this epic belongs to.';
    if (draft.startDate && draft.endDate && draft.startDate > draft.endDate) {
      next.endDate = 'End date must be on or after start date.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    onSubmit?.({
      title: draft.title.trim(),
      type: draft.type,
      projectId: draft.projectId,
      portfolioId: selectedProject?.portfolioId || null,
      programId: selectedProject?.programId || null,
      sprintId: draft.sprintId || null,
      ownerUserId: draft.ownerUserId || null,
      priority: draft.priority,
      status: draft.status,
      startDate: draft.startDate || null,
      endDate: draft.endDate || null,
      linkedObjectiveId: draft.linkedObjectiveId || null,
      missionKpiId: draft.missionKpiId || null,
      description: draft.description.trim(),
      acceptanceSummary: draft.acceptanceSummary.trim(),
      tags: draft.tags,
    });
  }

  return (
    <Drawer open={open} onClose={onClose} title="New Epic" width={560}>
      <DrawerForm onSubmit={handleSubmit} submitting={submitting}>
        <FormField label="Title" htmlFor="new-epic-title" required error={errors.title}>
          <TextInput
            id="new-epic-title"
            value={draft.title}
            onChange={(v) => update('title', v)}
            placeholder="e.g. Q4 Diagnostic Module Hardening"
            autoFocus
          />
        </FormField>

        <FormField label="Project" htmlFor="new-epic-project" required error={errors.projectId}>
          <Select
            id="new-epic-project"
            value={draft.projectId}
            onChange={(v) => update('projectId', v)}
            options={[
              { value: '', label: 'Select project…', disabled: true },
              ...projects.map((p) => ({ value: p.id, label: `${p.name} (${p.id})` })),
            ]}
          />
        </FormField>

        {selectedProject ? (
          <div className="form-hint">
            Portfolio <span className="mono">{selectedProject.portfolioId || '—'}</span> · Program{' '}
            <span className="mono">{selectedProject.programId || '—'}</span>
          </div>
        ) : null}

        <FormField label="Owner" htmlFor="new-epic-owner">
          <Select
            id="new-epic-owner"
            value={draft.ownerUserId}
            onChange={(v) => update('ownerUserId', v)}
            options={[
              { value: '', label: 'Unassigned' },
              ...users
                .slice(0, 60)
                .map((u) => ({ value: u.id, label: `${u.displayName || u.id} (${u.id})` })),
            ]}
          />
        </FormField>

        <FormField label="Sprint" htmlFor="new-epic-sprint">
          <Select
            id="new-epic-sprint"
            value={draft.sprintId}
            onChange={(v) => update('sprintId', v)}
            options={[
              { value: '', label: 'No sprint' },
              ...sprints.map((s) => ({ value: s.id, label: `${s.name || s.id} (${s.id})` })),
            ]}
            disabled={!draft.projectId}
          />
        </FormField>

        <div className="form-row-pair">
          <FormField label="Start date" htmlFor="new-epic-start">
            <DateInput
              id="new-epic-start"
              value={draft.startDate}
              onChange={(v) => update('startDate', v)}
            />
          </FormField>
          <FormField label="End date" htmlFor="new-epic-end" error={errors.endDate}>
            <DateInput
              id="new-epic-end"
              value={draft.endDate}
              onChange={(v) => update('endDate', v)}
            />
          </FormField>
        </div>

        <FormField label="Priority">
          <ChipMultiSelect
            options={PRIORITY_OPTIONS}
            selected={[draft.priority]}
            onToggle={(v) => update('priority', v)}
          />
        </FormField>

        <FormField label="Status" htmlFor="new-epic-status">
          <Select
            id="new-epic-status"
            value={draft.status}
            onChange={(v) => update('status', v)}
            options={STATUS_OPTIONS}
          />
        </FormField>

        <FormField label="Linked strategic objective" htmlFor="new-epic-objective">
          <Select
            id="new-epic-objective"
            value={draft.linkedObjectiveId}
            onChange={(v) => update('linkedObjectiveId', v)}
            options={[
              { value: '', label: 'None' },
              ...objectives.slice(0, 40).map((o) => ({
                value: o.id,
                label: `${o.name || o.title} (${o.id})`,
              })),
            ]}
          />
        </FormField>

        <FormField label="Mission KPI" htmlFor="new-epic-kpi">
          <Select
            id="new-epic-kpi"
            value={draft.missionKpiId}
            onChange={(v) => update('missionKpiId', v)}
            options={[
              { value: '', label: 'None' },
              ...missionKpis.slice(0, 40).map((k) => ({
                value: k.id,
                label: `${k.name || k.title} (${k.id})`,
              })),
            ]}
          />
        </FormField>

        <FormField label="Description" htmlFor="new-epic-description">
          <Textarea
            id="new-epic-description"
            value={draft.description}
            onChange={(v) => update('description', v)}
            placeholder="What does this epic deliver?"
            rows={3}
          />
        </FormField>

        <FormField label="Acceptance summary" htmlFor="new-epic-accept">
          <Textarea
            id="new-epic-accept"
            value={draft.acceptanceSummary}
            onChange={(v) => update('acceptanceSummary', v)}
            placeholder="How do we know it's done?"
            rows={3}
          />
        </FormField>

        <DrawerFormActions onCancel={onClose} submitLabel="Create epic" submitting={submitting} />
      </DrawerForm>
    </Drawer>
  );
}
